import { useCallback, useEffect, useRef, startTransition, useState } from "react";
import { Input, useComputedColorScheme } from "@mantine/core";
import EditorModule from "react-simple-code-editor";
// Vite 8 (Rolldown) no longer auto-unwraps CJS default exports
const Editor = EditorModule.default ?? EditorModule;
import { highlight, languages } from "prismjs/components/prism-core";
import "./language-pshuttle";
// import "./atom-dark.css";
import "./prism-vs.css"; // rsce-light prefixed
import "./prism-vs-dark.css"; // rsce-dark prefixed

const DEBOUNCE_MS = 200;
const MAX_HIGHLIGHT_LENGTH = 10000;

function escapeHtml(text) {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function PromptEditor({
  onChange,
  value,
  error,
  description,
  label,
  withAsterisk,
  ...props
}) {
  var theme = useComputedColorScheme();
  const [localValue, setLocalValue] = useState(value ?? "");
  const [pending, setPending] = useState(false);
  const timerRef = useRef(null);
  const pendingRef = useRef(false);
  const lastEmittedRef = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Sync from parent on external changes (initial load, programmatic updates)
  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      lastEmittedRef.current = value;
      if (!pendingRef.current) {
        setLocalValue(value ?? "");
      }
    }
  }, [value]);

  // Flush pending changes on unmount
  const localValueRef = useRef(localValue);
  localValueRef.current = localValue;
  useEffect(() => {
    return () => {
      if (pendingRef.current) {
        clearTimeout(timerRef.current);
        onChangeRef.current(localValueRef.current);
      }
    };
  }, []);

  const handleChange = useCallback((val) => {
    setLocalValue(val);
    setPending(true);
    pendingRef.current = true;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onChangeRef.current(val);
      lastEmittedRef.current = val;
      pendingRef.current = false;
      startTransition(() => setPending(false));
    }, DEBOUNCE_MS);
  }, []);

  const doHighlight = useCallback(
    (code) => {
      if (!code) return "";
      if (pending || !languages.pshuttle || code.length > MAX_HIGHLIGHT_LENGTH) {
        return escapeHtml(code);
      }
      return highlight(code, languages.pshuttle);
    },
    [pending],
  );

  return (
    <>
      <Input.Wrapper
        withAsterisk={withAsterisk}
        label={label}
        description={description}
        error={error}
      >
        <Editor
          {...props}
          onValueChange={handleChange}
          value={localValue}
          textareaClassName="rsce-textarea"
          preClassName={theme == "light" ? "rsce-light" : "rsce-dark"}
          background={theme == "light" ? "#fff" : "#0F1F3D"}
          highlight={doHighlight}
          padding={10}
          style={{
            fontFamily: "var(--mantine-font-family-monospace)",
            fontSize: 14,
            border:
              theme == "light"
                ? "calc(0.0625rem* var(--mantine-scale)) solid var(--mantine-color-dark-0)"
                : "calc(0.0625rem* var(--mantine-scale)) solid var(--mantine-color-dark-4)",
            borderRadius: "var(--mantine-radius-default)",
          }}
        />
      </Input.Wrapper>
    </>
  );
}

export default PromptEditor;
